/*
  Plain, translated texts for common server errors (plan, stage 5). The server
  answers in short English with field names ("cloaking_safe_url must be a valid
  http(s) URL"); the interface shows a sentence in the client's language
  instead. The plain English text is the translation key (./i18n/errorTexts.js).
  Unknown errors pass through unchanged.
*/
import { TRANSLATION_MAPS } from '../i18n';

const EXACT = {
  "Campaign not found":
    "This campaign was not found. It may have been deleted, or it belongs to another workspace.",
  "Offer not found":
    "This offer was not found. It may have been deleted.",
  "Domain not found":
    "This domain was not found. It may have been deleted.",
  "Landing page not found":
    "This landing page was not found. It may have been deleted.",
  "Traffic source not found":
    "This traffic source was not found. It may have been deleted.",
  "Affiliate network not found":
    "This affiliate network was not found. It may have been deleted.",
  "Stream not found":
    "This stream was not found. It may have been deleted.",
  "Domain does not belong to this workspace":
    "This domain belongs to another workspace. Choose one of your own domains.",
  "cloaking_safe_url must be a valid http(s) URL":
    "The safe page address must be a full link starting with http:// or https://.",
  "url is required":
    "Enter the address (URL).",
  "Enter a full http(s) URL":
    "Enter a full link starting with http:// or https://.",
  "ids must be a non-empty list":
    "Select at least one item.",
  "Invalid ZIP file":
    "This file is not a valid ZIP archive. Pack the files into a .zip and upload it again.",
  "White pages must be static (.html/.css/.js) — .php is not allowed":
    "A white page must be static files (.html, .css, .js); .php files are not allowed.",
  "Landing page storage limit exceeded":
    "There is no more room for landing files. Delete landings you do not use.",
  "Offer storage limit exceeded":
    "There is no more room for offer files. Delete offers you do not use.",
  "Cannot delete traffic source: it's used in campaigns":
    "This traffic source is used in campaigns. Change the source in those campaigns first.",
  "Cannot delete affiliate network: it's linked to offers":
    "This affiliate network is linked to offers. Unlink it from those offers first.",
  "Invalid hostname":
    "This is not a valid domain name. Enter it like track.example.com, without http:// and without a path.",
  "This hostname is reserved":
    "This domain name is reserved by the tracker. Use a domain of your own.",
  "Domain already registered":
    "This domain has already been added, in your workspace or another one.",
  "Domain must be verified first":
    "Verify the domain first: add the TXT record and press Verify.",
  "Domain pool has no healthy verified domains":
    "The domain pool has no verified working domains. Add or restore a domain in the pool.",
  "Invalid email address":
    "This e-mail address is not valid.",
  "start must be before end":
    "The start date must be before the end date.",
  "Dates must be YYYY-MM-DD":
    "Enter dates as YYYY-MM-DD.",
  "Invalid plan":
    "This plan does not exist. Reload the page and choose a plan again.",
  "Subscription required: your trial has ended or the subscription is inactive. Open Billing to choose a plan.":
    "No active subscription: campaigns are turned off and new items cannot be created. Open Billing to choose a plan.",
};

const PATTERNS = [
  [new RegExp("^Unknown or foreign ids in .+: use offers and pages of this workspace$"), "Some selected offers or pages belong to another workspace. Choose ones from this workspace."],
];

function currentLocale() {
  try {
    const saved = window.localStorage.getItem('locale');
    if (saved) return saved;
    return String(window.navigator.language || 'en').slice(0, 2).toLowerCase();
  } catch (_e) {
    return 'en';
  }
}

/** The plain English text for a server error, or null when unknown. */
export function plainErrorKey(detail) {
  if (typeof detail !== 'string') return null;
  const text = detail.trim();
  if (Object.prototype.hasOwnProperty.call(EXACT, text)) return EXACT[text];
  const hit = PATTERNS.find(([re]) => re.test(text));
  return hit ? hit[1] : null;
}

/** The plain text in the client's language, or null when unknown. */
export function translateApiError(detail, locale = currentLocale()) {
  const key = plainErrorKey(detail);
  if (!key) return null;
  const map = TRANSLATION_MAPS[locale] || {};
  return map[key] || key;
}
